import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarClock, Check, ChevronRight, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function StatusPill({ status, days }) {
  if (status === "active" && days > 14) return <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700">Active · {days} days left</Badge>;
  if (status === "active" || status === "expiring") return <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">Expiring · {days} days left</Badge>;
  if (status === "expired") return <Badge variant="outline" className="border-rose-300 bg-rose-50 text-rose-700">Expired — renew to continue</Badge>;
  return <Badge variant="outline" className="border-zinc-300 bg-zinc-100 text-zinc-600">No active plan</Badge>;
}

export default function MyPlan() {
  const navigate = useNavigate();
  const [my, setMy] = useState(null);
  const [plans, setPlans] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [action, setAction] = useState("renew");

  const load = async () => {
    try {
      const [m, p] = await Promise.all([
        api.get("/payments/my-plan"),
        api.get("/payments/plans"),
      ]);
      setMy(m.data);
      setPlans(p.data.items);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const onPick = (planId) => {
    setPickerOpen(false);
    navigate(`/app/checkout/${planId}?action=${action}`);
  };

  const startTrial = async (planId, planName) => {
    try {
      const { data } = await api.post("/payments/start-trial", { plan_id: planId });
      toast.success(`Trial activated! Valid for ${data.trial_days} days — until ${(data.valid_until || "").slice(0,10)}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  if (!my) return <div className="p-8 text-sm text-ink-muted">Loading…</div>;

  const current = my.plan;
  const isExpired = my.status === "expired";

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-plan-page">
      <div>
        <div className="section-label">Billing</div>
        <h1 className="font-heading text-3xl font-semibold">My subscription</h1>
        <p className="mt-1 text-sm text-ink-muted">{my.hospital?.name} · {my.hospital?.code}</p>
      </div>

      {isExpired && (
        <Card className="border-rose-300 bg-rose-50/60 p-4" data-testid="expired-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 text-rose-600" size={20} />
            <div>
              <h3 className="font-semibold text-rose-800">Subscription expired</h3>
              <p className="text-sm text-rose-900/80 mt-1">Your hospital is in <b>read-only mode</b>. Renewals, registrations, prescriptions, and billing entries are paused. Renew now to restore full access.</p>
              <Button size="sm" className="mt-3 bg-rose-600 hover:bg-rose-700" onClick={() => { setAction("renew"); setPickerOpen(true); }}>Renew now</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Current plan card */}
      <Card className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-ink-muted">Current plan</p>
            <h2 className="font-heading text-2xl font-semibold mt-1">{current?.name || "No plan assigned"}</h2>
            {current?.description && <p className="text-sm text-ink-muted mt-1">{current.description}</p>}
          </div>
          <StatusPill status={my.status} days={my.days_left} />
        </div>

        {current && (
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
            <div>
              <p className="text-xs text-ink-muted">Price</p>
              <p className="font-heading text-xl">₹ {Number(current.price_inr).toLocaleString("en-IN")}</p>
            </div>
            <div>
              <p className="text-xs text-ink-muted">Billing cycle</p>
              <p className="font-heading text-xl capitalize">{current.duration || "yearly"}</p>
            </div>
            <div>
              <p className="text-xs text-ink-muted">Valid until</p>
              <p className="font-heading text-xl">{my.valid_until ? my.valid_until.slice(0,10) : "—"}</p>
            </div>
            <div>
              <p className="text-xs text-ink-muted">Limits</p>
              <p className="text-sm">{current.branch_limit} branches · {current.user_limit} users · {current.storage_limit_gb}GB</p>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-2">
          <Button onClick={() => { setAction("renew"); setPickerOpen(true); }} className="bg-brand hover:bg-brand-700" data-testid="renew-btn">
            <CalendarClock size={14} className="mr-2" />Renew plan
          </Button>
          <Button onClick={() => { setAction("change"); setPickerOpen(true); }} variant="outline" data-testid="change-plan-btn">
            Change plan <ChevronRight size={14} className="ml-1" />
          </Button>
        </div>
      </Card>

      {/* Available plans grid */}
      <div>
        <h3 className="font-heading text-lg font-semibold mb-3">Available plans</h3>
        <div className="grid gap-4 md:grid-cols-4">
          {(() => {
            // Sort plans to the canonical sequence:
            //   Free Trial → Monthly → Quarterly → Half-Yearly → Yearly → other.
            // Trial plans always come first regardless of duration.
            const rank = { monthly: 1, quarterly: 2, half_yearly: 3, yearly: 4 };
            return [...plans].sort((a, b) => {
              const at = a.is_trial ? 0 : (rank[a.duration] || 9);
              const bt = b.is_trial ? 0 : (rank[b.duration] || 9);
              if (at !== bt) return at - bt;
              return (a.sort_order || 100) - (b.sort_order || 100);
            });
          })().map(p => {
            const trialUsed = (my.trial_claimed_plans || []).includes(p.id);
            const isTrialPlan = p.is_trial && p.trial_days > 0;
            return (
            <Card key={p.id} className={`relative p-5 flex flex-col ${current?.id === p.id ? "border-brand ring-2 ring-brand/20" : ""} ${isTrialPlan ? "border-emerald-400 bg-gradient-to-b from-emerald-50/60 to-white" : ""} ${p.is_recommended ? "border-amber-400 ring-2 ring-amber-300/40" : ""}`} data-testid={`avail-plan-${p.id.slice(0,6)}`}>
              {p.is_recommended && (
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-md" data-testid={`plan-recommended-${p.id.slice(0,6)}`}>
                  ★ Best for you
                </div>
              )}
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-heading text-lg font-semibold text-brand">{p.name}</p>
                  {p.code && <p className="text-[11px] font-mono text-ink-muted">{p.code}</p>}
                </div>
                {current?.id === p.id && <Badge className="bg-brand">Current</Badge>}
                {isTrialPlan && current?.id !== p.id && <Badge className="bg-emerald-600 text-[10px]">FREE · {p.trial_days}d</Badge>}
              </div>
              {isTrialPlan ? (
                <>
                  <p className="mt-3 font-heading text-3xl text-emerald-700">FREE</p>
                  <p className="text-xs text-ink-muted">{p.trial_days} days · No card needed</p>
                </>
              ) : (
                <>
                  <div className="mt-3 flex items-baseline gap-2">
                    <p className="font-heading text-3xl" data-testid={`plan-price-${(p.code || p.id).toString().slice(0,10)}`}>₹ {Number(p.price_inr).toLocaleString("en-IN")}</p>
                    {p.mrp_inr > 0 && p.mrp_inr > p.price_inr && (
                      <p className="text-sm text-ink-muted line-through" data-testid={`plan-mrp-${(p.code || p.id).toString().slice(0,10)}`}>₹ {Number(p.mrp_inr).toLocaleString("en-IN")}</p>
                    )}
                  </div>
                  {p.mrp_inr > 0 && p.mrp_inr > p.price_inr && (
                    <div className="mt-1 inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700" data-testid={`plan-disc-${(p.code || p.id).toString().slice(0,10)}`}>
                      {Math.round(((p.mrp_inr - p.price_inr) / p.mrp_inr) * 100)}% off
                    </div>
                  )}
                  <p className="mt-1 text-xs text-ink-muted capitalize">per {(p.duration || "year").replace("_", "-")}</p>
                </>
              )}
              {p.description && <p className="mt-2 text-sm text-ink-muted">{p.description}</p>}

              <div className="mt-4 flex-1 space-y-1.5 text-sm">
                <div className="flex items-center gap-2"><Check size={14} className="text-emerald-600" /> {p.branch_limit} branches</div>
                <div className="flex items-center gap-2"><Check size={14} className="text-emerald-600" /> {p.user_limit} users</div>
                <div className="flex items-center gap-2"><Check size={14} className="text-emerald-600" /> {p.storage_limit_gb} GB storage</div>
                <div className="flex items-center gap-2"><Check size={14} className="text-emerald-600" /> {(p.modules || []).length} modules</div>
                {(p.features || []).map((f, i) => <div key={i} className="flex items-start gap-2"><Check size={14} className="mt-0.5 shrink-0 text-emerald-600" /><span>{f}</span></div>)}
              </div>

              {isTrialPlan ? (
                <Button
                  className="mt-5 w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => startTrial(p.id, p.name)}
                  disabled={trialUsed || current?.id === p.id}
                  data-testid={`trial-${p.id.slice(0,6)}`}
                >
                  {trialUsed ? "Trial already used" : current?.id === p.id ? "Trial active" : `Start ${p.trial_days}-day free trial`}
                </Button>
              ) : (
                <Button
                  className="mt-5 w-full bg-brand hover:bg-brand-700"
                  onClick={() => onPick(p.id)}
                  disabled={current?.id === p.id && !isExpired && my.days_left > 30}
                  data-testid={`subscribe-${p.id.slice(0,6)}`}
                >
                  {current?.id === p.id ? "Renew this plan" : "Switch to this plan"}
                </Button>
              )}
            </Card>
            );
          })}
        </div>
      </div>

      {/* History */}
      <Card className="overflow-x-auto">
        <div className="p-4 border-b">
          <h3 className="font-heading text-lg font-semibold">Payment history</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-left text-[11px] uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Plan</th>
              <th className="px-4 py-3">Gateway</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3">Reference</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(my.history || []).map(t => (
              <tr key={t.id}>
                <td className="px-4 py-3 font-mono text-xs">{(t.created_at || "").slice(0, 16)}</td>
                <td className="px-4 py-3">{t.plan_name}</td>
                <td className="px-4 py-3 capitalize">{t.gateway}</td>
                <td className="px-4 py-3 capitalize">{t.action}</td>
                <td className="px-4 py-3 text-right">₹ {Number(t.amount_inr).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 text-center">
                  {t.status === "success"
                    ? <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700">{t.status}</Badge>
                    : <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">{t.status}</Badge>}
                </td>
                <td className="px-4 py-3 text-xs text-ink-muted">{t.reference || t.gateway_payment_id || "—"}</td>
              </tr>
            ))}
            {(my.history || []).length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-ink-muted">No payments yet.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{action === "renew" ? "Renew subscription" : "Change plan"}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {(() => {
              const rank = { monthly: 1, quarterly: 2, half_yearly: 3, yearly: 4 };
              return [...plans].sort((a, b) => {
                const at = a.is_trial ? 0 : (rank[a.duration] || 9);
                const bt = b.is_trial ? 0 : (rank[b.duration] || 9);
                if (at !== bt) return at - bt;
                return (a.sort_order || 100) - (b.sort_order || 100);
              });
            })().map(p => (
              <button key={p.id} onClick={() => onPick(p.id)} data-testid={`picker-plan-${p.id.slice(0,6)}`}
                className="w-full text-left rounded-lg border p-4 hover:border-brand hover:bg-secondary/40 transition">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-xs text-ink-muted">{p.branch_limit} branches · {p.user_limit} users · {p.duration}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-lg">₹ {Number(p.price_inr).toLocaleString("en-IN")}</div>
                    {p.mrp_inr > 0 && p.mrp_inr > p.price_inr && (
                      <div className="text-[10px] text-ink-muted">
                        <span className="line-through">₹{Number(p.mrp_inr).toLocaleString("en-IN")}</span>
                        <span className="ml-1 text-emerald-700 font-semibold">−{Math.round(((p.mrp_inr - p.price_inr) / p.mrp_inr) * 100)}%</span>
                      </div>
                    )}
                    <div className="text-[11px] text-ink-muted capitalize">per {(p.duration || "year").replace("_", "-")}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
